import React, { useCallback, useEffect, useMemo, useState } from 'react'

import Highcharts from 'highcharts'
import HighchartsSankey from 'highcharts/modules/sankey'
import HighchartsReact from 'highcharts-react-official'
import * as R from 'ramda'

import { Skeleton } from '@driftt/dds'
import { ChartEmptyState, CubeQueryStringAdapter } from '@driftt/dds.reports'

import { useTrackDrillDownClickEvent } from 'components/conversation/hooks/useTrackDrilldownClickEvent'
import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'
import { isNilOrEmpty } from 'utils/ramda'

import { useRoutingReportDrillDownQuery } from '../RoutingReportDrillDown/hooks'

import { ColorGradientRoot } from './ColorGradientRoot'
import { RoutingSankeyEmptyState } from './RoutingSankey.assets'
import { SANKEY_GRADIENT_COLORS } from './RoutingSankey.constants'
import { formatTooltipCopy } from './RoutingSankey.helpers'
import { SANKEY_NODES, SANKEY_OPTIONS } from './RoutingSankey.options'
import useRoutingSankey from './useRoutingSankey.hook'

import './RoutingSankey.styles.css'

HighchartsSankey(Highcharts)

export const RoutingSankey = () => {
  const [redirectToConvoDetail, setRedirectToConvoDetail] =
    useState<boolean>(false)

  const [filterMeasure, setFilterMeasure] = useState<string>('') // measure for drilldown data for cube query
  const [filterMeasureWeight, setFilterMeasureWeight] = useState<number>() // total number of records to be shown in drilldown

  const { drillDownCubeQuery } = useRoutingReportDrillDownQuery(filterMeasure)

  const { trackViewEvent } = useTrackDrillDownClickEvent('RoutingSankeyChart')

  const { data, isLoading } = useRoutingSankey()

  const redirectToConvoDetailPage = () => {
    if (redirectToConvoDetail) {
      const drillDownQueryString =
        CubeQueryStringAdapter.toQueryString(drillDownCubeQuery)

      const drillDownURL = `${CONVO_DETAIL_REPORT_PATH}?${drillDownQueryString}`
      window.open(drillDownURL, '_blank')

      setRedirectToConvoDetail(false)
    }
  }

  useEffect(redirectToConvoDetailPage, [
    drillDownCubeQuery,
    redirectToConvoDetail,
  ])

  const handleNodeClick = useCallback(
    ({ point }) => {
      if (!point.isNode) {
        const { options, weight } = point
        setFilterMeasure(options.name)
        setFilterMeasureWeight(weight)
        trackViewEvent({ totalRecords: filterMeasureWeight ?? 0 })
        setRedirectToConvoDetail(true)
      }
    },
    [filterMeasureWeight, trackViewEvent]
  )

  const options: Highcharts.Options = useMemo<Highcharts.Options>(
    () =>
      R.mergeDeepRight(SANKEY_OPTIONS, {
        series: [
          {
            keys: ['color', 'from', 'to', 'weight', 'name'],
            data: data,
            colors: SANKEY_GRADIENT_COLORS,
            type: 'sankey',
            name: 'Live chat journey',
            nodes: SANKEY_NODES,
            nodePadding: 60,
            cursor: 'default',
            events: {
              click: (e: any) => handleNodeClick(e),
            },
            dataLabels: {
              nodeFormatter: function () {
                return `${this.point.name} </br> ${this.point.sum}`
              },
              align: 'left',
              allowOverlap: true,
              enabled: true,
              style: {
                fontFamily: 'var(--drift-dds-fonts-primary)',
                fontSize: 'var(--drift-dds-fontSizes-h4)',
                fontWeight: 'var(--drift-dds-fontWeights-h4)',
                letterSpacing: 'var(--drift-dds-letterSpacings-h4)',
                color: 'var(--drift-dds-colors-h4)',
                textTransform: 'uppercase',
                textOutline: 'none',
              },
            },
          },
        ],
        tooltip: {
          useHTML: true,
          formatter: function () {
            return formatTooltipCopy(R.pathOr({}, ['point'], this))
          },
        },
      } as any),
    [data, handleNodeClick]
  )

  return (
    <div className="routing-report-sankey">
      <Skeleton isLoading={isLoading} height={400}>
        {isNilOrEmpty(data) ? (
          <ChartEmptyState>
            <RoutingSankeyEmptyState />
          </ChartEmptyState>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={options} />
        )}
      </Skeleton>
      <ColorGradientRoot />
    </div>
  )
}
